import React from 'react';
import { Box, ButtonBase, Paper, Typography, alpha, useTheme } from '@mui/material';
import { AccountBalanceOutlined, PaymentsOutlined } from '@mui/icons-material';
import { CashBankResponse } from '../../../types/finance';
import Amount from '../ledger/Amount';

/** What each cash and bank account holds today; picking one filters the movements. */
const CashBankAccounts: React.FC<{
  accounts: CashBankResponse['accounts'];
  selected: string;
  onSelect: (accountId: string) => void;
}> = ({ accounts, selected, onSelect }) => {
  const theme = useTheme();
  const total = accounts.reduce((s, a) => s + a.balancePaisa, 0);
  const cards = [{ _id: '', code: '', name: 'All cash & bank', balancePaisa: total }, ...accounts];

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: `repeat(${Math.min(cards.length, 4)}, 1fr)` }, gap: 1.5, mb: 2 }}>
      {cards.map(a => {
        const active = selected === a._id;
        const Icon = a.code.startsWith('111') ? PaymentsOutlined : AccountBalanceOutlined;
        return (
          <Paper
            key={a._id || 'all'}
            variant="outlined"
            component={ButtonBase}
            onClick={() => onSelect(a._id)}
            sx={{
              p: 2, borderRadius: 2, display: 'block', textAlign: 'left', width: '100%',
              borderColor: active ? 'primary.main' : 'divider',
              bgcolor: active ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
              transition: 'border-color .15s, background-color .15s',
              '&:hover': { borderColor: alpha(theme.palette.primary.main, 0.5) },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', mb: 0.75 }}>
              {a._id && <Icon sx={{ fontSize: 18 }} />}
              <Typography variant="caption" fontWeight={600} noWrap>
                {a.code && <Box component="span" sx={{ fontFamily: 'monospace', mr: 0.75 }}>{a.code}</Box>}
                {a.name}
              </Typography>
            </Box>
            <Amount paisa={a.balancePaisa} blankZero={false} variant="h6" fontWeight={700} color={a.balancePaisa < 0 ? 'error.main' : undefined} />
          </Paper>
        );
      })}
    </Box>
  );
};

export default CashBankAccounts;
